import BaseAPIs from "./base.apis";
import { iApiBasicResponse } from "../types/api";

export interface iWhatsNewTag {
    id: number;
    name: string;
    textColor: string;
    backgroundColor: string;
    highlighted?: boolean;
    isHighlighted?: boolean;
    is_highlighted?: boolean;
}

export interface iWhatsNewPost {
    id: number;
    title: string;
    description?: string | null;
    date?: string | null;
    tagIds?: number[];
    tag?: iWhatsNewTag | null;
    tags?: iWhatsNewTag[];
    /** Promotes the post into the top carousel; featured posts lead, then feed order fills to 3. */
    featured?: boolean;
    image?: string | null;
    cover_image?: string | null;
    thumbnail?: string | null;
    image_url?: string | null;
    ctaLabel?: string | null;
    ctaUrl?: string | null;
    cta_label?: string | null;
    cta_url?: string | null;
}

export interface iWhatsNewResponse extends iApiBasicResponse {
    title?: string;
    settings?: {
        showUnreadCount?: boolean;
        themeColor?: string;
        sidebarPosition?: string;
    };
    theme?: {
        themeColor?: string;
        textColor?: string;
    };
    posts?: iWhatsNewPost[];
    unread_count?: number;
}

export interface iFeedbackOption {
    name: string;
    type: string;
    emoji: string;
}

export interface iFeedbackWidgetResponse extends iApiBasicResponse {
    id?: number;
    name?: string;
    settings?: {
        question?: string;
        themeColor?: string;
    };
    options?: iFeedbackOption[];
}

export default class WhatsNewAPIs extends BaseAPIs {
    private static cachedGets: Record<string, Promise<iApiBasicResponse>> = {};

    private getAnnouncementsBaseURL = (): string => {
        return process.env.REACT_APP_ANNOUNCEMENTS_API_URL || "https://builder.emailwish.com";
    };

    private getFromAnnouncements = async <T extends iApiBasicResponse>(url: string): Promise<T> => {
        try {
            const response = await fetch(this.getAnnouncementsBaseURL() + url + "?ts=" + Date.now(), {
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
            const data = await response.json();

            return { statusCode: response.status, ...data } as T;
        } catch {
            return { statusCode: 999, errors: { network: "Network error!" } } as unknown as T;
        }
    };

    /**
     * Announcements and the feedback widget are static config, but the builder host answers in
     * ~1s, so a per-page-load cache keeps reopening instant and de-duplicates the header's
     * multiple widget instances. Failures are evicted so the next open retries.
     */
    private cachedGet = <T extends iApiBasicResponse>(url: string): Promise<T> => {
        if (!WhatsNewAPIs.cachedGets[url]) {
            WhatsNewAPIs.cachedGets[url] = this.getFromAnnouncements<T>(url).then((response) => {
                if (BaseAPIs.hasErrorNotification(response)) delete WhatsNewAPIs.cachedGets[url];

                return response;
            });
        }

        return WhatsNewAPIs.cachedGets[url] as Promise<T>;
    };

    private postToAnnouncements = async (url: string, payload: Record<string, unknown> = {}): Promise<iApiBasicResponse> => {
        try {
            const csrfResponse = await fetch(
                this.getAnnouncementsBaseURL() + "/common-resources/csrf-token?ts=" + Date.now(),
                { credentials: "include" }
            );
            const { token } = await csrfResponse.json();
            const response = await fetch(this.getAnnouncementsBaseURL() + url, {
                method: "POST",
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": token,
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({ ...payload, _token: token }),
            });
            const data = await response.json();

            return { statusCode: response.status, ...data };
        } catch {
            return { statusCode: 999, errors: { network: "Network error!" } };
        }
    };

    feed = async (): Promise<iWhatsNewResponse> => {
        return this.cachedGet<iWhatsNewResponse>("/whats-new");
    };

    unreadCount = async (): Promise<{ statusCode: number; count?: number }> => {
        return this.getFromAnnouncements<{ statusCode: number; count?: number }>("/whats-new/unread-count");
    };

    markRead = async (): Promise<iApiBasicResponse> => {
        return this.postToAnnouncements("/whats-new/read");
    };

    feedbackWidget = async (): Promise<iFeedbackWidgetResponse> => {
        return this.cachedGet<iFeedbackWidgetResponse>("/feedback-widget");
    };

    submitFeedback = async (payload: {
        type?: string;
        option_name?: string;
        emoji?: string;
        message?: string;
        from_url?: string;
    }): Promise<iApiBasicResponse> => {
        return this.postToAnnouncements("/feedback", payload);
    };
}
